import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { validateField, validatePhoneNumber, validateTelefonoAlternativoConjunto } from '../util/validation.js'

export const useMiPerfilForm = (initialData = {}, isDataLoaded = true) => {
  // Guardamos los datos iniciales en una ref para que no cambien
  const initialDataRef = useRef(initialData)

  const [apellido, setApellido] = useState('')
  const [nombre, setNombre] = useState('')
  const [tipoTelefonoPrincipal, setTipoTelefonoPrincipal] = useState('')
  const [numeroTelefonoPrincipal, setNumeroTelefonoPrincipal] = useState('')
  const [tipoTelefonoAlternativo, setTipoTelefonoAlternativo] = useState('')
  const [numeroTelefonoAlternativo, setNumeroTelefonoAlternativo] = useState('')
  const [isPhoneValid, setIsPhoneValid] = useState(true)
  // Indica si el usuario interactuó con el teléfono principal. El contorno verde
  // solo aplica por interacción del usuario, no por datos precargados válidos.
  const [isPhoneUserModified, setIsPhoneUserModified] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  // Snapshot reactivo del initialData: espeja initialDataRef para que hasChanges
  // se recalcule cuando syncInitialData actualiza el baseline tras guardar
  // (las refs no son reactivas y no disparan re-render)
  const [initialSnapshot, setInitialSnapshot] = useState(initialData)

  // Efecto para inicializar los valores precargados. Se ejecuta recién cuando
  // los datos del backend terminaron de cargar (isDataLoaded) y solo mientras
  // el formulario esté completamente vacío, de modo que nunca pise los valores
  // ingresados por el usuario. Nota: no se usa un flag de "edición del usuario"
  // porque InputDropdown del Design System dispara onChange automáticamente al
  // montar, lo que marcaría ese flag sin interacción real y bloquearía la carga.
  // Al inicializar también marca los campos obligatorios como "touched" y calcula
  // sus errores, de modo que los campos vacíos se muestren en rojo al ingresar.
  useEffect(() => {
    if (!isDataLoaded) return

    const statesAreEmpty =
      !apellido &&
      !nombre &&
      !tipoTelefonoPrincipal &&
      !numeroTelefonoPrincipal &&
      !tipoTelefonoAlternativo &&
      !numeroTelefonoAlternativo
    if (!statesAreEmpty) return

    initialDataRef.current = initialData
    setInitialSnapshot(initialData)

    const values = {
      apellido: initialData.apellido || '',
      nombre: initialData.nombre || '',
      tipoTelefonoPrincipal: initialData.tipoTelefonoPrincipal || '',
      numeroTelefonoPrincipal: initialData.numeroTelefonoPrincipal || '',
      tipoTelefonoAlternativo: initialData.tipoTelefonoAlternativo || '',
      numeroTelefonoAlternativo: initialData.numeroTelefonoAlternativo || '',
    }

    setApellido(values.apellido)
    setNombre(values.nombre)
    setTipoTelefonoPrincipal(values.tipoTelefonoPrincipal)
    setNumeroTelefonoPrincipal(values.numeroTelefonoPrincipal)
    setTipoTelefonoAlternativo(values.tipoTelefonoAlternativo)
    setNumeroTelefonoAlternativo(values.numeroTelefonoAlternativo)

    // Validación visual inicial de los campos obligatorios
    setTouched({
      apellido: true,
      nombre: true,
      tipoTelefonoPrincipal: true,
      numeroTelefonoPrincipal: true,
    })
    setErrors({
      apellido: validateField('apellido', values.apellido),
      nombre: validateField('nombre', values.nombre),
      tipoTelefonoPrincipal: validateField('tipoTelefonoPrincipal', values.tipoTelefonoPrincipal),
      numeroTelefonoPrincipal: validateField('numeroTelefonoPrincipal', values.numeroTelefonoPrincipal),
    })
  }, [
    initialData,
    isDataLoaded,
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
  ])

  const getError = useCallback((field) => (touched[field] ? errors[field] || null : null), [errors, touched])

  const getFieldError = useCallback((field) => getError(field), [getError])

  /**
   * Estado visual del campo para el prop `status` del Design System.
   * - 'error': campo touched con error de validación
   * - 'success': teléfono principal completado por el usuario con valor válido
   * - 'default': resto de los casos (incluye datos precargados válidos)
   */
  const getFieldStatus = useCallback(
    (field) => {
      const error = getError(field)
      if (error) return 'error'

      if (field === 'numeroTelefonoPrincipal' && isPhoneUserModified) {
        const value = numeroTelefonoPrincipal.trim()
        if (value !== '' && isPhoneValid && validatePhoneNumber(value) === null) {
          return 'success'
        }
      }

      return 'default'
    },
    [getError, isPhoneUserModified, isPhoneValid, numeroTelefonoPrincipal]
  )

  /**
   * Hints personalizados para el teléfono principal.
   * - Con error: undefined (el mensaje se muestra vía errorMessage del Field)
   * - Con interacción del usuario: undefined (el Design System muestra sus hints
   *   automáticos, ej. "Teléfono válido." en success al completar correctamente)
   * - Datos precargados sin interacción: [] (sin hints para no indicar validación)
   */
  /**
   * Estado visual del teléfono alternativo. El Design System calcula
   * internamente 'success' cuando el número es válido, lo que pintaría el
   * borde verde incluso con datos precargados. Se pasa un status explícito:
   * - 'error': campo touched con error (formato inválido o conjunto incompleto)
   * - 'default': resto de los casos (incluye datos precargados válidos)
   */
  const getAltPhoneStatus = useCallback(
    (field) => {
      const error = getError(field)
      if (error) return 'error'
      return 'default'
    },
    [getError]
  )

  /**
   * Hints del teléfono alternativo:
   * - Con error de formato: undefined (el mensaje se muestra vía errorMessage)
   * - Resto: [] (sin hints para no indicar validación sobre datos precargados)
   */
  const getAltPhoneHints = useCallback(() => {
    if (getError('numeroTelefonoAlternativo')) return undefined
    return []
  }, [getError])

  const getPhoneHints = useCallback(() => {
    if (getError('numeroTelefonoPrincipal')) return undefined
    if (isPhoneUserModified) return undefined
    return []
  }, [getError, isPhoneUserModified])

  const handleBlur = useCallback((field, value) => {
    const fieldError = validateField(field, value)
    setTouched((current) => ({ ...current, [field]: true }))
    setErrors((current) => ({
      ...current,
      [field]: fieldError,
    }))
  }, [])

  const handleApellidoChange = useCallback(
    (e) => {
      const value = e?.target?.value ?? e
      setApellido(value)
      if (touched.apellido) {
        setErrors((current) => ({
          ...current,
          apellido: validateField('apellido', value),
        }))
      }
    },
    [touched.apellido]
  )

  const handleNombreChange = useCallback(
    (e) => {
      const value = e?.target?.value ?? e
      setNombre(value)
      if (touched.nombre) {
        setErrors((current) => ({
          ...current,
          nombre: validateField('nombre', value),
        }))
      }
    },
    [touched.nombre]
  )

  // Nota: InputDropdown del Design System dispara onChange automáticamente al
  // montar para sincronizar su selección. Por eso solo se recalcula el error si
  // el campo ya está touched (validación inicial o interacción previa), evitando
  // parpadeos del estado de error durante la carga de los datos precargados.
  const handleTipoTelefonoPrincipalChange = useCallback(
    (value) => {
      setTipoTelefonoPrincipal(value)
      if (touched.tipoTelefonoPrincipal) {
        setErrors((current) => ({
          ...current,
          tipoTelefonoPrincipal: validateField('tipoTelefonoPrincipal', value),
        }))
      }
    },
    [touched.tipoTelefonoPrincipal]
  )

  const handleNumeroTelefonoPrincipalChange = useCallback((value) => {
    setNumeroTelefonoPrincipal(value)
    setIsPhoneUserModified(true)
    setTouched((current) => ({ ...current, numeroTelefonoPrincipal: true }))
    setErrors((current) => ({
      ...current,
      numeroTelefonoPrincipal: validateField('numeroTelefonoPrincipal', value),
    }))
  }, [])

  const handlePhoneDataChange = useCallback(({ isValid }) => {
    setIsPhoneValid(isValid)
  }, [])

  /**
   * Recalcula los errores del conjunto teléfono alternativo (tipo + número)
   * y los aplica sobre el estado de errors. Se usa en cada cambio de alguno
   * de los dos campos para mantener la validación cruzada en vivo.
   * @param {string} tipo - Tipo de teléfono alternativo
   * @param {string} numero - Número de teléfono alternativo
   */
  const applyAltPhoneErrors = useCallback((tipo, numero) => {
    const conjuntoErrors = validateTelefonoAlternativoConjunto(tipo, numero)
    // Ambos campos del conjunto pasan a touched: si el usuario completa uno,
    // el error del otro (campo faltante o formato inválido) debe verse de inmediato
    setTouched((current) => ({
      ...current,
      tipoTelefonoAlternativo: true,
      numeroTelefonoAlternativo: true,
    }))
    setErrors((current) => ({
      ...current,
      tipoTelefonoAlternativo: conjuntoErrors.tipo,
      numeroTelefonoAlternativo: conjuntoErrors.numero,
    }))
  }, [])

  const handleTipoTelefonoAlternativoChange = useCallback(
    (value) => {
      setTipoTelefonoAlternativo(value)
      setTouched((current) => ({ ...current, tipoTelefonoAlternativo: true }))
      applyAltPhoneErrors(value, numeroTelefonoAlternativo)
    },
    [applyAltPhoneErrors, numeroTelefonoAlternativo]
  )

  const handleNumeroTelefonoAlternativoChange = useCallback(
    (value) => {
      setNumeroTelefonoAlternativo(value)
      setTouched((current) => ({ ...current, numeroTelefonoAlternativo: true }))
      applyAltPhoneErrors(tipoTelefonoAlternativo, value)
    },
    [applyAltPhoneErrors, tipoTelefonoAlternativo]
  )

  const isFormValid = useMemo(() => {
    const isApellidoValid = apellido.trim().length >= 2
    const isNombreValid = nombre.trim().length >= 2
    const isTipoTelefonoValid = tipoTelefonoPrincipal.trim() !== ''
    const isNumeroTelefonoValid =
      numeroTelefonoPrincipal.trim() !== '' && isPhoneValid && validatePhoneNumber(numeroTelefonoPrincipal) === null

    // El teléfono alternativo se valida como un único conjunto:
    // - ambos campos vacíos => válido (no obligatorio)
    // - ambos completos y número válido => válido
    // - cualquier combinación parcial o número inválido => inválido
    const conjuntoAlt = validateTelefonoAlternativoConjunto(tipoTelefonoAlternativo, numeroTelefonoAlternativo)
    const isTelefonoAlternativoValid = conjuntoAlt.tipo === null && conjuntoAlt.numero === null

    return (
      isApellidoValid && isNombreValid && isTipoTelefonoValid && isNumeroTelefonoValid && isTelefonoAlternativoValid
    )
  }, [
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    isPhoneValid,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
  ])

  const hasChanges = useMemo(() => {
    const initial = initialSnapshot
    return (
      apellido !== (initial.apellido || '') ||
      nombre !== (initial.nombre || '') ||
      tipoTelefonoPrincipal !== (initial.tipoTelefonoPrincipal || '') ||
      numeroTelefonoPrincipal !== (initial.numeroTelefonoPrincipal || '') ||
      tipoTelefonoAlternativo !== (initial.tipoTelefonoAlternativo || '') ||
      numeroTelefonoAlternativo !== (initial.numeroTelefonoAlternativo || '')
    )
  }, [
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
    initialSnapshot,
  ])

  /**
   * Sincroniza en caliente el "estado inicial" del formulario con los datos
   * recién guardados, sin necesidad de recargar la página. hasChanges
   * vuelve a false y el botón Guardar cambios se deshabilita.
   * @param {Object} newData - Datos guardados que pasan a ser el nuevo initialData
   */
  const syncInitialData = useCallback((newData) => {
    if (!newData) return
    const merged = { ...initialDataRef.current, ...newData }
    initialDataRef.current = merged
    setInitialSnapshot(merged)
  }, [])

  const validateAllFields = useCallback(() => {
    const apellidoError = validateField('apellido', apellido)
    const nombreError = validateField('nombre', nombre)
    const tipoTelefonoError = validateField('tipoTelefonoPrincipal', tipoTelefonoPrincipal)
    const numeroTelefonoError = validateField('numeroTelefonoPrincipal', numeroTelefonoPrincipal)
    const conjuntoAlt = validateTelefonoAlternativoConjunto(tipoTelefonoAlternativo, numeroTelefonoAlternativo)

    setTouched({
      apellido: true,
      nombre: true,
      tipoTelefonoPrincipal: true,
      numeroTelefonoPrincipal: true,
      tipoTelefonoAlternativo: true,
      numeroTelefonoAlternativo: true,
    })

    setErrors({
      apellido: apellidoError,
      nombre: nombreError,
      tipoTelefonoPrincipal: tipoTelefonoError,
      numeroTelefonoPrincipal: numeroTelefonoError,
      tipoTelefonoAlternativo: conjuntoAlt.tipo,
      numeroTelefonoAlternativo: conjuntoAlt.numero,
    })

    return !(
      apellidoError ||
      nombreError ||
      tipoTelefonoError ||
      numeroTelefonoError ||
      conjuntoAlt.tipo ||
      conjuntoAlt.numero
    )
  }, [
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
  ])

  const resetForm = useCallback(() => {
    const initial = initialDataRef.current
    setApellido(initial.apellido || '')
    setNombre(initial.nombre || '')
    setTipoTelefonoPrincipal(initial.tipoTelefonoPrincipal || '')
    setNumeroTelefonoPrincipal(initial.numeroTelefonoPrincipal || '')
    setTipoTelefonoAlternativo(initial.tipoTelefonoAlternativo || '')
    setNumeroTelefonoAlternativo(initial.numeroTelefonoAlternativo || '')
    setTouched({})
    setErrors({})
    setIsPhoneValid(true)
    setIsPhoneUserModified(false)
  }, [])

  return {
    apellido,
    nombre,
    tipoTelefonoPrincipal,
    numeroTelefonoPrincipal,
    tipoTelefonoAlternativo,
    numeroTelefonoAlternativo,
    isFormValid,
    hasChanges,

    setTipoTelefonoPrincipal,
    setNumeroTelefonoPrincipal,
    setTipoTelefonoAlternativo,
    setNumeroTelefonoAlternativo,

    handleApellidoChange,
    handleNombreChange,
    handleTipoTelefonoPrincipalChange,
    handleNumeroTelefonoPrincipalChange,
    handleTipoTelefonoAlternativoChange,
    handleNumeroTelefonoAlternativoChange,
    handleBlur,
    handlePhoneDataChange,

    getFieldStatus,
    getFieldError,
    getPhoneHints,
    getAltPhoneStatus,
    getAltPhoneHints,
    validateAllFields,
    resetForm,
    syncInitialData,
  }
}
